import { formatMoney } from "@vouch/shared";
import { CheckCircle2, ShieldQuestion, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button, Card, Spinner } from "../components/ui";

interface WitnessDetails {
  witnessId: string;
  status: string;
  amount: number;
  currency: string;
  description: string | null;
  inviterName: string;
  accepted: boolean;
}

export function WitnessConfirmPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [details, setDetails] = useState<WitnessDetails | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error" | "done">("loading");
  const [outcome, setOutcome] = useState<"confirmed" | "declined" | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setState("error");
      return;
    }
    void (async () => {
      try {
        const res = await api<{ witness: WitnessDetails }>(
          `/witness/confirm?token=${encodeURIComponent(token)}`,
          { anonymous: true },
        );
        setDetails(res.witness);
        setState(res.witness.status === "pending" ? "ready" : "done");
        if (res.witness.status !== "pending") {
          setOutcome(res.witness.status as "confirmed" | "declined");
        }
      } catch {
        setState("error");
      }
    })();
  }, [token]);

  const respond = async (action: "confirm" | "decline"): Promise<void> => {
    setBusy(true);
    try {
      const res = await api<{ status: "confirmed" | "declined" }>("/witness/confirm", {
        method: "POST",
        body: { token, action },
        anonymous: true,
      });
      setOutcome(res.status);
      setState("done");
    } catch {
      setState("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-brand-500">Vouch</h1>
          <p className="text-sm text-slate-500">Witness confirmation</p>
        </div>
        <Card>
          {state === "loading" && <Spinner />}
          {state === "error" && (
            <div className="text-center">
              <XCircle className="mx-auto mb-2 text-red-500" size={32} />
              <p className="text-sm text-slate-600">This link is invalid or has expired.</p>
            </div>
          )}
          {state === "done" && (
            <div className="text-center">
              {outcome === "confirmed" ? (
                <CheckCircle2 className="mx-auto mb-2 text-green-500" size={32} />
              ) : (
                <XCircle className="mx-auto mb-2 text-slate-400" size={32} />
              )}
              <p className="text-sm text-slate-600">
                You have <strong>{outcome}</strong> this transaction. Thank you.
              </p>
            </div>
          )}
          {state === "ready" && details && (
            <div className="space-y-4">
              <div className="text-center">
                <ShieldQuestion className="mx-auto mb-2 text-brand-500" size={32} />
                <p className="text-sm text-slate-600">
                  <strong>{details.inviterName}</strong> asked you to witness a loan.
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4 text-center">
                <p className="text-2xl font-bold text-brand-500">
                  {formatMoney(details.amount, details.currency)}
                </p>
                {details.description && (
                  <p className="mt-1 text-sm text-slate-500">{details.description}</p>
                )}
              </div>
              {details.accepted ? (
                <>
                  <p className="text-center text-xs text-slate-400">
                    Only confirm if you can vouch that this loan is accurate.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      className="flex-1"
                      onClick={() => void respond("decline")}
                      disabled={busy}
                    >
                      Decline
                    </Button>
                    <Button className="flex-1" onClick={() => void respond("confirm")} disabled={busy}>
                      {busy ? "Saving…" : "Confirm"}
                    </Button>
                  </div>
                </>
              ) : (
                <p className="rounded-lg bg-amber-50 p-3 text-center text-sm text-amber-700">
                  The other party hasn't accepted this loan yet. You'll be able to confirm once they do.
                </p>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
